import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";

/**
 * Diálogo del tope diario de uso alcanzado.
 *
 * Se abre solo, escuchando el evento `smartdx:usage-limit` que emite el
 * interceptor de `get-client` ante un 429 del backend. No hay que cablearlo en
 * cada caller: cubre generar, generar async, token de dictado y transcribir.
 *
 * Por qué existe: hasta ahora el 429 caía en el manejo genérico de error y el
 * médico leía "No pudimos generar la transcripción diaria", que se confunde con
 * una falla del sistema y lleva a reintentar en loop. El tope no es una falla:
 * es una decisión de producto, y tiene una salida concreta (pedir más).
 *
 * El texto del mensaje lo manda el backend (usage_limits._OVER_LIMIT_*) para
 * que quede una sola fuente de verdad, y para poder cambiarlo sin redeploy.
 */

// Fallback si el evento llega sin texto (no debería pasar, pero el diálogo
// nunca debe quedar mudo).
const FALLBACK =
  "Alcanzaste tu límite diario de uso. Se renueva mañana. " +
  "Si necesitas más, ponte en contacto con el equipo de SmartDx.";

const UsageLimitDialog = () => {
  const [message, setMessage] = useState(null);

  const onLimit = useCallback((event) => {
    setMessage(String(event?.detail || FALLBACK));
  }, []);

  useEffect(() => {
    window.addEventListener("smartdx:usage-limit", onLimit);
    return () => window.removeEventListener("smartdx:usage-limit", onLimit);
  }, [onLimit]);

  const close = () => setMessage(null);

  return (
    <Dialog
      open={Boolean(message)}
      onClose={close}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          background: "var(--bg-elevated)",
          borderRadius: "16px",
          border: "1px solid var(--border-soft)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.2,
          fontSize: 16,
          fontWeight: 700,
          color: "var(--text-primary)",
          pb: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 30,
            height: 30,
            borderRadius: "10px",
            background: "rgba(var(--color-warning-rgb),0.16)",
            color: "var(--color-warning)",
            flexShrink: 0,
          }}
        >
          <HourglassBottomIcon sx={{ fontSize: 18 }} />
        </Box>
        Límite diario alcanzado
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <Box
          sx={{
            fontSize: 13.5,
            lineHeight: 1.6,
            color: "var(--text-secondary)",
          }}
        >
          {message}
        </Box>
        <Box
          sx={{
            mt: 1.5,
            fontSize: 12,
            lineHeight: 1.5,
            color: "var(--gray-faint)",
          }}
        >
          Tu trabajo está guardado. Puedes seguir escribiendo y editando a mano
          sin ninguna restricción.
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={close}
          variant="contained"
          disableElevation
          sx={{
            textTransform: "none",
            borderRadius: "10px",
            fontWeight: 600,
            background: "var(--color-primary)",
            "&:hover": { background: "var(--color-primary-hover)" },
          }}
        >
          Entendido
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UsageLimitDialog;
